import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { ShoppingCart, Loader2, ChevronRight, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RupiahInput } from "@/components/RupiahInput";
import { CompanyPicker, PAY_STATUS_LABEL, SELECT_CLS, today, useCompanies } from "@/components/SalesShared";
import { toast } from "sonner";
import { PayPartsFields, payPartsBody, payPartsError } from "@/components/TxPayParts";

const EMPTY = { date: today(), nota_number: "", customer_id: "", customer_name: "", customer_address: "", customer_phone: "", amount: "", pay_status: "cash", company_id: "", account_number: "", due_days: "", cash_part: "", transfer_part: "" };
const addDays = (d, n) => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + n); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };

function CustomerSearch({ customers, value, selected, onType, onPick }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const s = value.trim().toLowerCase();
  const digits = s.replace(/\D/g, "");
  const list = customers.filter((c) => !s || c.name.toLowerCase().includes(s) || (digits && (c.phone || "").replace(/\D/g, "").includes(digits)) || (c.address || "").toLowerCase().includes(s)).slice(0, 8);
  const pick = (c) => { onPick(c); setOpen(false); };
  const onKey = (e) => {
    if (!open || !list.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); pick(list[hi]); }
    if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="relative mt-1.5">
      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input value={value} required autoComplete="off" placeholder="Ketik nama / No. HP customer, atau isi nama customer baru"
        onChange={(e) => { onType(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey}
        className="pl-9 pr-28" data-testid="tx-customer" />
      <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${selected ? "bg-emerald-100 text-emerald-800" : value.trim() ? "bg-amber-100 text-amber-800" : "hidden"}`} data-testid="tx-customer-state">
        {selected ? "Tersimpan" : "Customer baru"}
      </span>
      {open && !selected && list.length > 0 && (
        <div className="absolute z-30 mt-1 w-full rounded-md border bg-white shadow-lg max-h-72 overflow-y-auto" data-testid="tx-customer-results">
          {list.map((c, i) => (
            <button type="button" key={c.id} onMouseDown={(e) => { e.preventDefault(); pick(c); }} onMouseEnter={() => setHi(i)}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-sm ${i === hi ? "bg-secondary" : ""}`} data-testid={`tx-customer-option-${c.id}`}>
              <span className="min-w-0"><span className="font-medium block truncate">{c.name}</span><span className="text-xs text-muted-foreground block truncate">{c.phone || "-"}{c.address ? ` · ${c.address}` : ""}</span></span>
              {c.remaining > 0 && <span className="text-[11px] font-mono text-rose-600 shrink-0">Piutang {formatRupiah(c.remaining)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TxForm({ companies, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const loadCust = useCallback(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadCust(); }, [loadCust]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value, ...(["customer_name", "customer_phone"].includes(k) ? { customer_id: "" } : {}) }));
  const pickCustomer = (c) => {
    setF((p) => ({ ...p, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || "", customer_address: c.address || "" }));
  };
  const setAcc = useCallback((v) => setF((p) => ({ ...p, account_number: v })), []);
  const submit = async (e) => {
    e.preventDefault();
    const perr = payPartsError(f, Number(f.amount) || 0);
    if (perr) { toast.error(perr); return; }
    const days = parseInt(f.due_days, 10);
    if (f.pay_status === "kredit" && !(days > 0)) { toast.error("Isi jangka waktu kredit (hari)"); return; }
    setBusy(true);
    try {
      const { due_days, cash_part, transfer_part, ...body } = f;
      await api.post("/transactions", { ...body, ...payPartsBody(f), amount: Number(f.amount) || 0, company_id: f.company_id || null, due_date: f.pay_status === "kredit" ? addDays(f.date, days) : null, customer_id: f.customer_id || null });
      toast.success("Transaksi disimpan.");
      setF({ ...EMPTY, date: f.date }); onSaved(); loadCust();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5">
      <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Form Input Transaksi</h3>
      <form onSubmit={submit} className="space-y-3" data-testid="tx-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={set("date")} required className="mt-1.5" data-testid="tx-date" /></div>
          <div><Label>Nomor Nota</Label><Input value={f.nota_number} onChange={set("nota_number")} required className="mt-1.5" data-testid="tx-nota" /></div>
          <div className="sm:col-span-2">
            <Label>Nama Pelanggan</Label>
            <CustomerSearch customers={customers} value={f.customer_name} selected={!!f.customer_id}
              onType={(v) => setF((p) => ({ ...p, customer_name: v, customer_id: "" }))} onPick={pickCustomer} />
          </div>
          <div><Label>Alamat Pelanggan{!f.customer_id && <span className="text-red-500"> *</span>}</Label><Input value={f.customer_address} onChange={set("customer_address")} required={!f.customer_id} className="mt-1.5" data-testid="tx-address" /></div>
          <div><Label>No. HP{!f.customer_id && <span className="text-red-500"> *</span>}</Label><Input type="tel" value={f.customer_phone} onChange={set("customer_phone")} required={!f.customer_id} className="mt-1.5" data-testid="tx-phone" /></div>
          <div><Label>Jumlah Tagihan</Label><RupiahInput value={f.amount} onChange={set("amount")} required placeholder="0" className="mt-1.5 font-mono" data-testid="tx-amount" /></div>
          <div>
            <Label>Status Bayar</Label>
            <select value={f.pay_status} onChange={set("pay_status")} className={`${SELECT_CLS} mt-1.5`} data-testid="tx-pay-status">
              {Object.entries(PAY_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          {f.pay_status === "kredit" && (
            <div>
              <Label>Jangka Waktu (hari) <span className="text-red-500">*</span></Label>
              <Input type="number" min="1" step="1" inputMode="numeric" value={f.due_days} onChange={set("due_days")} required placeholder="contoh: 30" className="mt-1.5 font-mono" data-testid="tx-due-days" />
              <p className="text-xs text-muted-foreground mt-1" data-testid="tx-due-date-preview">
                Jatuh tempo: <span className="font-medium text-foreground">{parseInt(f.due_days, 10) > 0 && f.date ? formatDate(addDays(f.date, parseInt(f.due_days, 10))) : "—"}</span>
              </p>
            </div>
          )}
        </div>
        {f.pay_status === "transfer" && <CompanyPicker companies={companies} value={f.company_id} onChange={(v) => setF((p) => ({ ...p, company_id: v }))} account={f.account_number} onAccountChange={setAcc} testId="tx-company" />}
        <PayPartsFields f={f} setF={setF} companies={companies} amount={Number(f.amount) || 0} testId="tx" />
        <div className="flex justify-end">
          <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="tx-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Transaksi"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default function Transactions() {
  const navigate = useNavigate();
  const companies = useCompanies();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><ShoppingCart className="h-6 w-6 text-brand" /> Input Transaksi</h2>
        <p className="text-sm text-muted-foreground">Catat nota penjualan ke pelanggan. Transaksi kredit otomatis masuk ke halaman Piutang.</p>
      </div>
      <TxForm companies={companies} onSaved={() => {}} />
      <Button variant="outline" onClick={() => navigate("/transaction-list")} className="border-brand/40 text-brand" data-testid="tx-goto-list">Lihat Daftar Transaksi <ChevronRight className="h-4 w-4 ml-1" /></Button>
    </div>
  );
}
